<template>
  <section class="cleaner-activity">
    <CleanerActivityHeader
      :lastUpdatedLabel="isHistoryView ? '' : lastUpdatedLabel"
      :lastUpdatedAbsolute="isHistoryView ? '' : lastUpdatedAbsolute"
      :dataStatus="isHistoryView ? null : effectiveDataStatus"
    />

    <div v-if="!siteId" class="cleaner-activity__empty">
      <p class="cleaner-activity__empty-title">{{ $t('please_select_company_site') }}</p>
      <p class="cleaner-activity__empty-desc">{{ $t('ca_empty_select_site_hint') }}</p>
    </div>

    <CleanerHistory
      v-else-if="isHistoryView"
      :siteId="siteId"
      :companyToday="companyToday"
      :workingAreas="workingAreas"
      :workingAreaRoomMap="workingAreaRoomMap"
    />

    <template v-else>
      <CleanerActivityToolbar>
        <CleanerActivityPeriodFilter
          :date="pendingDate"
          :shiftId="pendingShiftId"
          :shifts="availableShifts"
          :maxDate="companyToday"
          :isLive="isLiveMode"
          :canReturnToLive="!!currentShift"
          :canApply="canApplyPeriod"
          :disabled="loading"
          @date-change="handleDateChange"
          @shift-change="handleShiftChange"
          @apply="applyPeriod"
          @return-live="returnToLive"
        />
        <template v-slot:actions>
          <CleanerActivityFilterMenu
            :activeCount="activeFilterCount"
            :disabled="loading"
            @clear="clearActivityFilter"
          >
            <CleanerActivityFilters
              v-model="activityFilter"
              :workingAreas="workingAreas"
              :cleaners="filteredCleaners"
              :disabled="loading"
            />
          </CleanerActivityFilterMenu>
        </template>
      </CleanerActivityToolbar>

      <Loading :height="'52vh'" v-if="loading" />

      <template v-else>
        <CleanerActivityKpis :summary="displaySummary" />

        <CleanerActivityAttentionStrip
          :cleaners="displayCleaners"
          :slaMinutes="slaMinutes"
          :shiftStart="shiftRange ? shiftRange.start : null"
          :idleThresholdMinutes="idleThresholdMinutes"
          :nowTick="nowTick"
          :hidden="!!timelineEmptyState || !isLiveMode"
          @focus-cleaner="focusCleaner"
        />

        <div class="cleaner-activity__main">
          <div class="cleaner-activity__timeline">
            <div v-if="timelineEmptyState" class="cleaner-activity__empty">
              <p class="cleaner-activity__empty-title">{{ $t(timelineEmptyState.title) }}</p>
              <p class="cleaner-activity__empty-desc">{{ $t(timelineEmptyState.desc) }}</p>
            </div>
            <CleanerActivityTimeline
              v-else-if="timelineRange"
              ref="timeline"
              :cleaners="displayCleaners"
              :filterRoomIds="effectiveRoomIdList"
              :rangeStart="timelineRange.start"
              :rangeEnd="timelineRange.end"
              :companyGmt="companyGmt"
              :slaMinutes="slaMinutes"
              :shiftStart="shiftRange ? shiftRange.start : null"
              :isLiveMode="isLiveMode"
              :referenceTimeMs="referenceTimeMs"
              :highlightEmployeeId="highlightEmployeeId"
            />
          </div>
        </div>
      </template>
    </template>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { getCleanerActivity, emptyCleanerActivitySummary } from '../services/performanceService'
import shiftService from '../../control-panel/services/shiftService'
import realTimeService from '../../real-time/services/realTimeService'
import utilService from '../../common/services/utilService'
import Loading from '../../common/components/Loading'
import CleanerActivityHeader from './cleaner-activity/CleanerActivityHeader.vue'
import CleanerActivityToolbar from './cleaner-activity/CleanerActivityToolbar.vue'
import CleanerActivityPeriodFilter from './cleaner-activity/CleanerActivityPeriodFilter.vue'
import CleanerActivityFilterMenu from './cleaner-activity/CleanerActivityFilterMenu.vue'
import CleanerActivityFilters from './cleaner-activity/CleanerActivityFilters.vue'
import CleanerActivityKpis from './cleaner-activity/CleanerActivityKpis.vue'
import CleanerActivityAttentionStrip from './cleaner-activity/CleanerActivityAttentionStrip.vue'
import CleanerActivityTimeline from './cleaner-activity/CleanerActivityTimeline.vue'
import CleanerHistory from './cleaner-activity/history/CleanerHistory.vue'
import ajax from '../../common/services/httpService'
import {
  emptyActivityFilter,
  hasActiveActivityFilter,
  countActiveActivityFilters,
  getEffectiveRoomIds,
  cleanerMatchesLocationFilter,
  scopeCleanersToRooms,
  filterCleanersByIds,
  enrichCleanersMetrics,
  sortCleaners,
  summarizeCleanerActivity,
} from './cleaner-activity/cleanerActivityFilter'
import { resolveIdleThresholdMinutes, toLocalDateStr } from './cleaner-activity/cleanerActivityUtils'

// Live auto-refresh cadence for "today".
const POLL_INTERVAL_MS = 30000
// After this long without a successful refresh, treat displayed data as stale.
const STALE_THRESHOLD_MS = POLL_INTERVAL_MS * 2
// Cadence for re-rendering the relative "updated X ago" label.
const NOW_TICK_INTERVAL_MS = 1000
// Debounce applied when the selected site changes before refetching.
const SITE_CHANGE_DEBOUNCE_MS = 50

export default {
  components: {
    Loading,
    CleanerActivityHeader,
    CleanerActivityToolbar,
    CleanerActivityPeriodFilter,
    CleanerActivityFilterMenu,
    CleanerActivityFilters,
    CleanerActivityKpis,
    CleanerActivityAttentionStrip,
    CleanerActivityTimeline,
    CleanerHistory,
  },
  data() {
    return {
      cleaners: [],
      summary: emptyCleanerActivitySummary(),
      loading: false,
      requestId: 0,
      fetchTimer: null,
      pollTimer: null,
      currShift: null,
      currentShift: null,
      followLive: true,
      shifts: [],
      // Applied period: what the timeline currently shows and what fetches are built from.
      selectedDate: '',
      selectedShiftId: null,
      // Staged period: what the date/shift inputs show. Only Apply copies it onto the applied
      // period, so picking a date and then a shift costs one request instead of two.
      pendingDate: '',
      pendingShiftId: null,
      // Company-local date range for the API query (spans the active shift window).
      range: null,
      // The shift window used for the timeline X-axis (start_hour to end_hour).
      shiftRange: null,
      lastUpdated: null,
      /** 'live' | 'stale' | 'error' | null */
      dataStatus: null,
      hasSuccessfulFetch: false,
      // Ticks every second so the "updated X ago" label stays current.
      nowTick: Date.now(),
      nowTimer: null,
      /**
       * Why the timeline is empty: no_shift | no_cleaners | error_forbidden | error_load
       * @type {null | 'no_shift' | 'no_cleaners' | 'error_forbidden' | 'error_load'}
       */
      emptyReason: null,
      activityFilter: emptyActivityFilter(),
      workingAreas: [],
      workingAreaRoomMap: {},
      highlightEmployeeId: null,
      highlightTimer: null,
    }
  },
  computed: {
    ...mapGetters(['filterSelected', 'filterOptions']),
    siteId() {
      return this.filterSelected && this.filterSelected.site
    },
    view() {
      return this.$route.query.view === 'history' ? 'history' : 'shift'
    },
    isHistoryView() {
      return this.view === 'history'
    },
    companyGmt() {
      return this.currShift ? this.currShift.gmt : null
    },
    slaMinutes() {
      return this.currShift ? this.currShift.max_sla : null
    },
    companyToday() {
      const gmt = this.currShift?.gmt ?? this.currentShift?.gmt ?? this.shifts[0]?.gmt
      return utilService.companyLocalDateStr(new Date(), gmt) || new Date().toISOString().slice(0, 10)
    },
    // Driven by the staged date so the dropdown repopulates while choosing, without fetching.
    availableShifts() {
      if (!this.pendingDate) return this.shifts
      const pendingDay = this.weekArrivalDay(this.pendingDate)
      return this.shifts.filter((shift) => {
        const days = this.parseWeekArrivals(shift.week_arrivals)
        return !days.length || days.includes(pendingDay)
      })
    },
    hasPendingPeriodChange() {
      return this.pendingDate !== this.selectedDate
        || Number(this.pendingShiftId) !== Number(this.selectedShiftId)
    },
    canApplyPeriod() {
      if (this.loading || !this.pendingDate || this.pendingShiftId == null) return false
      return this.hasPendingPeriodChange
    },
    selectedShift() {
      const selectedId = Number(this.selectedShiftId)
      return this.shifts.find((shift) => Number(shift.id) === selectedId)
        || (this.currentShift && Number(this.currentShift.id) === selectedId ? this.currentShift : null)
    },
    selectedWindow() {
      return realTimeService.getShiftWindowForDate(this.selectedShift, this.selectedDate)
    },
    liveShiftDate() {
      if (!this.currentShift) return this.companyToday
      const window = realTimeService.getCurrentShiftWindow(this.currentShift)
      return window
        ? (utilService.companyLocalDateStr(window.start, this.currentShift.gmt) || this.companyToday)
        : this.companyToday
    },
    isLiveMode() {
      if (!this.currentShift || !this.selectedWindow) return false
      const selectedId = Number(this.selectedShiftId)
      const currentShifts = this.currentShift.currentShifts || [this.currentShift]
      const isCurrentShift = currentShifts.some((shift) => Number(shift.id) === selectedId)
      const now = Date.now()
      return isCurrentShift
        && now >= this.selectedWindow.start.getTime()
        && now < this.selectedWindow.end.getTime()
    },
    referenceTimeMs() {
      if (this.isLiveMode) return this.nowTick
      return this.shiftRange ? this.shiftRange.end.getTime() : this.nowTick
    },
    idleThresholdMinutes() {
      return resolveIdleThresholdMinutes(this.currShift && this.currShift.clean_time)
    },
    timelineRange() {
      return this.shiftRange || this.range
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) return ''
      const seconds = Math.max(0, Math.round((this.nowTick - this.lastUpdated.getTime()) / 1000))
      if (seconds < 5) return this.$t('just_now')
      if (seconds < 60) return `${seconds}s ${this.$t('ago')}`
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes}m ${this.$t('ago')}`
      const hours = Math.floor(minutes / 60)
      return `${hours}h ${this.$t('ago')}`
    },
    lastUpdatedAbsolute() {
      if (!this.lastUpdated) return ''
      const gmt = this.currShift && this.currShift.gmt != null && this.currShift.gmt !== '' ? this.currShift.gmt : null
      return utilService.formatTimeInCompanyTz(this.lastUpdated, gmt, '')
    },
    isDataStaleByAge() {
      if (!this.isLiveMode || !this.lastUpdated || !this.hasSuccessfulFetch) return false
      return this.nowTick - this.lastUpdated.getTime() > STALE_THRESHOLD_MS
    },
    effectiveDataStatus() {
      if (this.dataStatus === 'error') return 'error'
      if (this.dataStatus === 'stale' || this.isDataStaleByAge) return 'stale'
      if (this.dataStatus === 'live') return 'live'
      return null
    },
    hasActiveActivityFilter() {
      return hasActiveActivityFilter(this.activityFilter)
    },
    activeFilterCount() {
      return countActiveActivityFilters(this.activityFilter)
    },
    effectiveRoomIds() {
      return getEffectiveRoomIds(this.filterOptions, this.activityFilter, this.workingAreaRoomMap)
    },
    /** Cleaners after the location (working-area / room) filter — the pool the cleaner picker lists. */
    filteredCleaners() {
      if (!this.cleaners.length) return []
      const effectiveRoomIds = this.effectiveRoomIds
      if (!effectiveRoomIds) return this.cleaners
      return this.cleaners.filter((cleaner) =>
        cleanerMatchesLocationFilter(cleaner, effectiveRoomIds, this.activityFilter),
      )
    },
    effectiveRoomIdList() {
      return this.effectiveRoomIds ? [...this.effectiveRoomIds] : null
    },
    displayCleaners() {
      let result = scopeCleanersToRooms(this.filteredCleaners, this.effectiveRoomIds)
      result = enrichCleanersMetrics(result, {
        slaMinutes: this.slaMinutes,
        nowMs: this.referenceTimeMs,
      })
      result = filterCleanersByIds(result, this.activityFilter.cleanerIds)
      return sortCleaners(result, this.activityFilter.sortKey)
    },
    displaySummary() {
      if (!this.hasActiveActivityFilter) return this.summary
      return summarizeCleanerActivity(this.displayCleaners, {
        slaMinutes: this.slaMinutes,
        nowMs: this.referenceTimeMs,
      })
    },
    timelineEmptyState() {
      if (this.displayCleaners.length) return null
      if (this.cleaners.length && this.hasActiveActivityFilter) {
        return { title: 'ca_empty_no_filter_match', desc: 'ca_empty_no_filter_match_hint' }
      }
      const reason = this.emptyReason
        || (this.effectiveDataStatus === 'error' ? 'error_load' : null)
      if (!reason) return null
      const states = {
        no_shift: { title: 'no_active_shift', desc: 'check_site_and_shift' },
        no_cleaners: { title: 'no_cleaners_found', desc: 'ca_empty_no_cleaners_hint' },
        error_forbidden: { title: 'ca_empty_access_denied', desc: 'ca_empty_access_denied_hint' },
        error_load: { title: 'failed_to_load_data', desc: 'ca_empty_load_error_hint' },
      }
      return states[reason] || states.error_load
    },
  },
  watch: {
    filteredCleaners(nextOptions) {
      const allowedIds = new Set((nextOptions || []).map((cleaner) => Number(cleaner.employeeId)))
      const cleanerIds = (this.activityFilter.cleanerIds || []).filter((id) => allowedIds.has(Number(id)))
      if (cleanerIds.length !== (this.activityFilter.cleanerIds || []).length) {
        this.activityFilter = { ...this.activityFilter, cleanerIds }
      }
    },
    // Reload shifts and reset the period whenever the selected site changes.
    siteId: {
      immediate: true,
      handler() {
        this.resetFetchState()
        this.clearActivityFilter()
        this.loadWorkingAreas()
        if (this.fetchTimer) clearTimeout(this.fetchTimer)
        this.fetchTimer = setTimeout(() => this.initializePeriod(), SITE_CHANGE_DEBOUNCE_MS)
      },
    },
    // Live polling is paused while History is open; catch up as soon as the shift view returns.
    isHistoryView(isHistory) {
      if (!isHistory && this.followLive && !this.loading) this.refreshLiveData()
    },
  },
  mounted() {
    // Live updates: refresh the active shift and its data (paused for historical selections).
    this.pollTimer = setInterval(() => {
      if (document.visibilityState === 'hidden' || !this.followLive || this.isHistoryView) return
      this.refreshLiveData()
    }, POLL_INTERVAL_MS)
    this.nowTimer = setInterval(() => { this.nowTick = Date.now() }, NOW_TICK_INTERVAL_MS)
    this.onVisibilityChange = () => {
      // Refetch as soon as the user returns so they don't stare at stale data.
      if (document.visibilityState === 'visible' && this.followLive && !this.isHistoryView) this.refreshLiveData()
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    if (this.fetchTimer) clearTimeout(this.fetchTimer)
    if (this.pollTimer) clearInterval(this.pollTimer)
    if (this.nowTimer) clearInterval(this.nowTimer)
    if (this.highlightTimer) clearTimeout(this.highlightTimer)
    if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    clearActivityFilter() {
      this.activityFilter = emptyActivityFilter()
    },
    focusCleaner(employeeId) {
      this.highlightEmployeeId = Number(employeeId)
      if (this.highlightTimer) clearTimeout(this.highlightTimer)
      this.$nextTick(() => {
        const timeline = this.$refs.timeline
        if (timeline && timeline.scrollToCleaner) timeline.scrollToCleaner(this.highlightEmployeeId)
      })
      this.highlightTimer = setTimeout(() => {
        this.highlightEmployeeId = null
        this.highlightTimer = null
      }, 4000)
    },
    async loadWorkingAreas() {
      if (!this.siteId) {
        this.workingAreas = []
        this.workingAreaRoomMap = {}
        return
      }
      try {
        const rows = await ajax.get('workingArea', null, { siteId: this.siteId })
        const workingAreaRoomMap = {}
        this.workingAreas = (rows || []).map((row) => {
          const roomIds = (row.rooms || [])
            .map((room) => Number(room.room_id || room.id))
            .filter(Number.isFinite)
          workingAreaRoomMap[row.id] = roomIds
          return {
            id: row.id,
            area_name: row.area_name,
            roomIds,
          }
        })
        this.workingAreaRoomMap = workingAreaRoomMap
      } catch (error) {
        console.error('[CleanerActivity] failed to load working areas', error)
        this.workingAreas = []
        this.workingAreaRoomMap = {}
      }
    },
    resetFetchState() {
      this.dataStatus = null
      this.hasSuccessfulFetch = false
      this.lastUpdated = null
      this.emptyReason = null
    },
    clearActivityData() {
      this.cleaners = []
      this.summary = emptyCleanerActivitySummary()
      this.currShift = null
      this.range = null
      this.shiftRange = null
    },
    parseWeekArrivals(value) {
      if (Array.isArray(value)) return value.map(Number).filter(Number.isFinite)
      if (!value) return []
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : []
      } catch (error) {
        return []
      }
    },
    weekArrivalDay(dateStr) {
      const date = new Date(`${dateStr}T00:00:00Z`)
      return Number.isNaN(date.getTime()) ? null : date.getUTCDay() + 1
    },
    /** Accepts the picker's Date or an API date string; yields 'YYYY-MM-DD' or ''. */
    normalizeDate(value) {
      const fromDate = toLocalDateStr(value)
      if (fromDate) return fromDate
      const match = String(value || '').match(/^\d{4}-\d{2}-\d{2}/)
      return match ? match[0] : ''
    },
    async initializePeriod() {
      if (!this.siteId) {
        this.shifts = []
        this.currentShift = null
        this.followLive = true
        this.selectedDate = ''
        this.selectedShiftId = null
        this.syncPendingToApplied()
        this.loading = false
        return
      }

      const siteId = this.siteId
      this.loading = true
      try {
        const [shifts, currentShift] = await Promise.all([
          shiftService.getShifts(siteId),
          shiftService.getCurrentShift(siteId),
        ])
        if (Number(this.siteId) !== Number(siteId)) return

        this.shifts = Array.isArray(shifts) ? shifts : []
        this.currentShift = currentShift || null
        this.followLive = true
        this.selectedDate = currentShift ? this.liveShiftDate : this.companyToday
        // Stage the date before reading availableShifts, which derives the default shift from it.
        this.pendingDate = this.selectedDate
        const defaultShift = currentShift || this.availableShifts[0] || null
        this.selectedShiftId = defaultShift ? defaultShift.id : null
        this.pendingShiftId = this.selectedShiftId
        await this.fetchData(false)
      } catch (error) {
        if (Number(this.siteId) !== Number(siteId)) return
        console.error('[CleanerActivity] failed to initialize period', error)
        this.handleFetchError(error)
      } finally {
        if (Number(this.siteId) === Number(siteId)) this.loading = false
      }
    },
    // Date and shift only stage a selection; nothing is requested until applyPeriod runs.
    handleDateChange(value) {
      const date = this.normalizeDate(value)
      if (!date || date > this.companyToday || date === this.pendingDate) return
      this.pendingDate = date
      if (!this.availableShifts.some((shift) => Number(shift.id) === Number(this.pendingShiftId))) {
        this.pendingShiftId = this.availableShifts[0]?.id ?? null
      }
    },
    handleShiftChange(value) {
      const shiftId = Array.isArray(value) ? value[0] : value
      if (shiftId == null || Number(shiftId) === Number(this.pendingShiftId)) return
      this.pendingShiftId = shiftId
    },
    applyPeriod() {
      if (!this.canApplyPeriod) return
      this.selectedDate = this.pendingDate
      this.selectedShiftId = this.pendingShiftId
      this.followLive = this.isLiveMode
      this.clearActivityFilter()
      this.fetchData(false)
    },
    syncPendingToApplied() {
      this.pendingDate = this.selectedDate
      this.pendingShiftId = this.selectedShiftId
    },
    async returnToLive() {
      this.followLive = true
      this.clearActivityData()
      this.resetFetchState()
      this.loading = true
      try {
        await this.refreshLiveData(false)
        this.syncPendingToApplied()
      } finally {
        this.loading = false
      }
    },
    async refreshLiveData(silent = true) {
      if (!this.siteId || !this.followLive) return
      try {
        const currentShift = await shiftService.getCurrentShift(this.siteId, silent ? { silent: true } : {})
        if (!this.followLive) return
        // A background poll must not discard a period the user is in the middle of staging.
        const shouldSyncPending = !this.hasPendingPeriodChange
        this.currentShift = currentShift || null
        if (!currentShift) {
          this.clearActivityData()
          this.resetFetchState()
          this.emptyReason = 'no_shift'
          return
        }
        this.selectedDate = this.liveShiftDate
        this.selectedShiftId = currentShift.id
        if (shouldSyncPending) this.syncPendingToApplied()
        await this.fetchData(silent)
      } catch (error) {
        console.error('[CleanerActivity] live refresh failed', error)
        this.handleFetchError(error)
      }
    },
    emptyReasonFromError(error) {
      if (error && error.status === 403) return 'error_forbidden'
      const msg = error && error.message ? String(error.message).toLowerCase() : ''
      if (msg.includes('access denied') || msg.includes('forbidden')) return 'error_forbidden'
      return 'error_load'
    },
    handleFetchSuccess(summary, cleaners) {
      this.cleaners = cleaners
      this.summary = summary
      this.lastUpdated = new Date()
      this.hasSuccessfulFetch = true
      this.dataStatus = this.isLiveMode ? 'live' : null
      this.emptyReason = cleaners.length ? null : 'no_cleaners'
    },
    handleFetchError(error) {
      if (this.hasSuccessfulFetch && this.lastUpdated) {
        this.dataStatus = 'stale'
        return
      }
      this.dataStatus = 'error'
      this.clearActivityData()
      this.emptyReason = this.emptyReasonFromError(error)
    },
    async fetchData(silent = false) {
      if (!this.siteId) {
        this.clearActivityData()
        this.resetFetchState()
        this.loading = false
        return
      }
      const requestId = ++this.requestId
      if (!silent) {
        this.loading = true
        this.cleaners = []
        this.summary = emptyCleanerActivitySummary()
        this.lastUpdated = null
        this.hasSuccessfulFetch = false
        this.dataStatus = null
        this.emptyReason = null
      }
      const requestOptions = silent ? { silent: true } : {}
      try {
        const shift = this.selectedShift
        const shiftWindow = this.selectedWindow
        if (!shift || !shiftWindow) {
          this.clearActivityData()
          this.resetFetchState()
          this.emptyReason = 'no_shift'
          return
        }

        this.currShift = shift
        this.shiftRange = shiftWindow
        const range = this.shiftRange
          ? realTimeService.getShiftQueryDateRange(shift, this.shiftRange)
          : null
        this.range = range

        if (!this.shiftRange || !range) {
          this.cleaners = []
          this.summary = emptyCleanerActivitySummary()
          this.emptyReason = 'no_shift'
          return
        }
        const { summary, cleaners } = await getCleanerActivity(this.siteId, {
          time: { timeFrom: range.timeFrom, timeTo: range.timeTo },
          maxSla: shift.max_sla,
          shiftId: shift.id,
          shiftStart: this.shiftRange.start.toISOString(),
          shiftEnd: this.shiftRange.end.toISOString(),
        }, requestOptions)
        if (requestId !== this.requestId) return
        this.handleFetchSuccess(summary, cleaners)
      } catch (error) {
        if (requestId !== this.requestId) return
        console.error('[CleanerActivity] fetch failed', error)
        this.handleFetchError(error)
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use "./cleaner-activity/caControls" as *;

.cleaner-activity {
  padding: 16px 12px;

  &__main {
    margin-bottom: 16px;
  }

  &__timeline {
    background: #fff;
    border: 1px solid #eef0f3;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    padding: 12px 14px;
    min-width: 0;
  }

  &__empty {
    @include ca-empty-state;
  }
}

@media (max-width: 767px) {
  .cleaner-activity {
    padding: 12px 8px;

    &__timeline {
      padding: 8px 6px;
      border-radius: 8px;
    }
  }
}
</style>
